<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import NavBadge from '@js/components/navigation/NavBadge.vue';
import { AnimatePresence, Motion } from 'motion-v';
import { CollapsibleRoot, CollapsibleTrigger } from 'reka-ui';
import { inject, onMounted, onUnmounted, ref } from 'vue';

const {
    title,
    icon = null,
    active = false,
    defaultOpen = false,
    level = 0,
    setCurrentDisclosure = undefined,
    alwaysOpen = false,
    pinValue = undefined,
    pingType = 'primary',
    animatePing = false,
} = defineProps({
    title: {
        type: String,
        required: true,
    },
    icon: {
        type: [Object, Function],
        required: false,
    },
    active: {
        type: Boolean,
        required: false,
    },
    defaultOpen: {
        type: Boolean,
        required: false,
    },
    level: {
        type: Number,
        required: false,
    },
    setCurrentDisclosure: {
        type: Function,
        required: false,
    },
    alwaysOpen: {
        type: Boolean,
        required: false,
    },
    pinValue: {
        type: [Number, String],
        required: false,
    },
    pingType: {
        type: String,
        required: false,
    },
    animatePing: {
        type: Boolean,
        required: false,
    },
});

const menuGroup = inject('menuGroup', null);

const isOpen = ref(active || defaultOpen || alwaysOpen);

const disclosure = {
    close: () => {
        isOpen.value = false;
    },
    get alwaysOpen() {
        return alwaysOpen;
    },
};

if (menuGroup) {
    const unregisterDisclosure = menuGroup.registerDisclosure(disclosure);

    onUnmounted(unregisterDisclosure);
}

function notifyCurrentDisclosure() {
    const handler = setCurrentDisclosure ?? menuGroup?.setCurrentDisclosure;

    handler?.(disclosure);
}

onMounted(() => {
    if (active) {
        notifyCurrentDisclosure();
    }
});
</script>

<template>
    <CollapsibleRoot v-model:open="isOpen" class="flex flex-col">
        <CollapsibleTrigger
            class="group relative flex w-full items-center gap-2 truncate rounded-lg p-2 duration-150 hover:bg-indigo-600 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            :class="{
                'bg-gray-200 text-slate-600': isOpen || active,
                '!overflow-visible': pinValue !== undefined,
            }"
            :title="title"
            @click="notifyCurrentDisclosure"
        >
            <component
                v-if="icon && level === 0"
                :is="icon"
                class="size-5 shrink-0 grow-0 text-gray-400 group-hover:text-white"
            />

            <div
                :class="{
                    'truncate text-left': level === 0,
                    'pl-7': level === 1,
                }"
            >
                {{ title }}
            </div>

            <CaretDownIcon
                class="ml-auto size-4 shrink-0 grow-0 text-gray-400 duration-150 group-hover:text-white group-data-[state=open]:rotate-180"
            />

            <NavBadge
                :pin-value="pinValue"
                :ping-type="pingType"
                :animate-ping="animatePing"
                position="left"
            />
        </CollapsibleTrigger>

        <AnimatePresence :initial="false">
            <Motion
                v-if="isOpen"
                :initial="{ height: 0, opacity: 0 }"
                :animate="{ height: 'auto', opacity: 1 }"
                :exit="{ height: 0, opacity: 0 }"
                :transition="{ duration: 0.2 }"
                class="-mx-1 overflow-hidden px-1"
            >
                <div class="flex flex-col gap-2 pt-2">
                    <slot :disclosure="disclosure" />
                </div>
            </Motion>
        </AnimatePresence>
    </CollapsibleRoot>
</template>
